import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// Image compression utility - BETTER QUALITY
async function compressImage(file: File, maxWidth = 1600, quality = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const maxDim = Math.max(width, height);
        if (maxDim > maxWidth) {
          const scale = maxWidth / maxDim;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas context failed'));
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const outputQuality = file.type === 'image/png' ? undefined : quality;
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas to blob failed'));
        }, outputType, outputQuality);
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function isValidImageUrl(url: string): boolean {
  return /^https?:\/\/.+\.(jpg|jpeg|png|webp|gif|avif)(\?.*)?$/i.test(url) ||
    url.startsWith('data:image/') ||
    /^https?:\/\/.+\/product-images\/.+/i.test(url);
}

interface Message {
  id: string;
  role: 'bot' | 'user';
  text: string;
  type?: 'text' | 'quick-replies' | 'image-preview' | 'product-summary' | 'stock-input' | 'video-preview';
  quickReplies?: string[];
  imageUrl?: string;
  videoUrl?: string;
  productDraft?: DraftProduct;
}

interface ColorVariant {
  name: string;
  hex: string;
  price: number;
  image: string;
  images: string[];
  stock?: number;
}

interface DraftProduct {
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  description: string;
  sizes: string[];
  colors: ColorVariant[];
  images: string[];
  thumbnail: string;
  stock: number;
  stockBreakdown: Record<string, unknown>;
  brand: string;
  tags: string[];
  video?: string;
  videoThumbnail?: string;
}

type BotStep =
  | 'welcome'
  | 'ask-name'
  | 'ask-category'
  | 'ask-price'
  | 'ask-original-price'
  | 'ask-brand'
  | 'ask-description'
  | 'ask-sizes'
  | 'ask-colors'
  | 'ask-color-images'
  | 'ask-color-specific-image'
  | 'ask-image'
  | 'ask-more-images'
  | 'ask-stock-type'
  | 'ask-stock-simple'
  | 'ask-stock-per-size'
  | 'ask-stock-per-color'
  | 'ask-video'           // NEW: 5-second video step
  | 'review-product'
  | 'saving'
  | 'done';

const CATEGORIES = ['Track Pants', 'T-Shirts', 'Jeans', 'Hoodies', 'Shorts'];
const COMMON_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const COMMON_COLORS = [
  { name: 'Black', hex: '#000000' },
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Navy Blue', hex: '#1E3A5F' },
  { name: 'Grey', hex: '#6B7280' },
  { name: 'Green', hex: '#22C55E' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Brown', hex: '#92400E' },
];

interface AIProductBotProps {
  onProductAdded: () => void;
  onClose: () => void;
}

export default function AIProductBot({ onProductAdded, onClose }: AIProductBotProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [step, setStep] = useState<BotStep>('welcome');
  const [draft, setDraft] = useState<Partial<DraftProduct>>({});
  const [pendingColorIdx, setPendingColorIdx] = useState(0);
  const [pendingSizes, setPendingSizes] = useState<string[]>([]);
  const [sizeStockMap, setSizeStockMap] = useState<Record<string, string>>({});
  const [colorStockMap, setColorStockMap] = useState<Record<string, string>>({});
  const [stockType, setStockType] = useState<'simple' | 'per-size' | 'per-color'>('simple');
  const [saving, setSaving] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const addBotMessage = useCallback((text: string, extra?: Partial<Message>) => {
    const msg: Message = { id: generateId(), role: 'bot', text, type: 'text', ...extra };
    setMessages(prev => [...prev, msg]);
  }, []);

  const addUserMessage = useCallback((text: string) => {
    setMessages(prev => [...prev, { id: generateId(), role: 'user', text }]);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      addBotMessage(
        'Namaste! Main hoon tumhara AI Product Assistant 🤖\n\nMain tumhare saath product listing ka poora kaam karunga — naam se lekar video tak sab set kar dunga!\n\n📸 **Photos + 🎬 5-Sec Video** — dono upload kar sakte ho phone se!\n\nStart karein?',
        {
          type: 'quick-replies',
          quickReplies: ['Haan, shuru karo! 🚀', 'Kya kya poochega?'],
        }
      );
      setStep('welcome');
    }, 300);
  }, [addBotMessage]);

  const handleQuickReply = (reply: string) => {
    addUserMessage(reply);
    handleInput(reply);
  };

  // ── Ask video step helper ──
  const askVideoStep = useCallback((currentDraft: Partial<DraftProduct>) => {
    // Update draft state before asking video step
    setDraft(currentDraft);
    addBotMessage(
      '🎬 **5-Second Product Video** (Optional)\n\nEk chhota sa video add kar sakte ho — customers ko product zyada clearly dikhega!\n\n**Rules:**\n• Max 5 seconds\n• Max 20MB file size\n• MP4 / WebM format\n• Phone se record karke upload karo\n\nVideo add karni hai?',
      {
        type: 'quick-replies',
        quickReplies: ['🎬 Video Upload Karo', '⏭️ Skip — Video Nahi Chahiye'],
      }
    );
    setStep('ask-video');
  }, [addBotMessage]);

  const handleInput = useCallback((value: string) => {
    const val = value.trim();
    if (!val) return;
    setInput('');

    switch (step) {
      case 'welcome': {
        if (val.toLowerCase().includes('kya') || val.toLowerCase().includes('what')) {
          addBotMessage(
            'Main tumse yeh sab poochunga:\n1. Product ka naam\n2. Category\n3. Price & MRP\n4. Brand\n5. Description\n6. Sizes\n7. Colors\n8. Product photos 📸\n9. Stock\n10. 5-second video 🎬 (optional)\n\nPhir preview dikhaunga — approve karo toh live!',
            { type: 'quick-replies', quickReplies: ['Theek hai, shuru karo! 🚀'] }
          );
        } else {
          addBotMessage('Shuruaat karte hain! 🎉\n\nPehle mujhe product ka naam batao:');
          setStep('ask-name');
        }
        break;
      }

      case 'ask-name': {
        if (val.length < 3) { addBotMessage('Naam thoda aur descriptive rakho — kam se kam 3 characters!'); return; }
        setDraft(prev => ({ ...prev, name: val }));
        addBotMessage(`"${val}" — achha naam! 👍\n\nAb category select karo:`, { type: 'quick-replies', quickReplies: CATEGORIES });
        setStep('ask-category');
        break;
      }

      case 'ask-category': {
        const cat = CATEGORIES.find(c => c.toLowerCase() === val.toLowerCase()) ?? val;
        setDraft(prev => ({ ...prev, category: cat }));
        addBotMessage(`Category: ${cat} ✅\n\nAb selling price batao (₹ mein, sirf number):`);
        setStep('ask-price');
        break;
      }

      case 'ask-price': {
        const price = parseInt(val.replace(/[^0-9]/g, ''), 10);
        if (!price || price <= 0) { addBotMessage('Sahi price daalo — sirf number jaise 499 ya 1299'); return; }
        setDraft(prev => ({ ...prev, price }));
        addBotMessage(`Selling price: ₹${price} ✅\n\nOriginal / MRP price daalo (ya "same" likho agar discount nahi):`);
        setStep('ask-original-price');
        break;
      }

      case 'ask-original-price': {
        let origPrice = draft.price ?? 0;
        if (val.toLowerCase() !== 'same') origPrice = parseInt(val.replace(/[^0-9]/g, ''), 10) || (draft.price ?? 0);
        setDraft(prev => ({ ...prev, originalPrice: origPrice }));
        const discount = origPrice > (draft.price ?? 0) ? Math.round((1 - (draft.price ?? 0) / origPrice) * 100) : 0;
        addBotMessage(`MRP: ₹${origPrice}${discount > 0 ? ` (${discount}% discount)` : ''} ✅\n\nBrand ka naam daalo (ya "skip" karo):`);
        setStep('ask-brand');
        break;
      }

      case 'ask-brand': {
        const brand = val.toLowerCase() === 'skip' ? '' : val;
        setDraft(prev => ({ ...prev, brand }));
        addBotMessage(`${brand ? `Brand: ${brand} ✅` : 'Brand skip.'}\n\nProduct ka description likho (material, fit, features):`);
        setStep('ask-description');
        break;
      }

      case 'ask-description': {
        if (val.length < 10) { addBotMessage('Thoda aur detail mein likho!'); return; }
        setDraft(prev => ({ ...prev, description: val }));
        addBotMessage('Description set! ✅\n\nKaunse sizes available hain?', {
          type: 'quick-replies',
          quickReplies: [...COMMON_SIZES, 'Free Size', 'Sabhi Sizes (S-XXXL)', 'Skip'],
        });
        setPendingSizes([]);
        setStep('ask-sizes');
        break;
      }

      case 'ask-sizes': {
        if (val === 'Skip' || val === 'Free Size') {
          const sizes = val === 'Free Size' ? ['Free Size'] : [];
          setDraft(prev => ({ ...prev, sizes }));
          addBotMessage(`${val === 'Free Size' ? 'Free Size' : 'Sizes skip'} ✅\n\nColors available hain?`, {
            type: 'quick-replies', quickReplies: [...COMMON_COLORS.map(c => c.name), 'No Colors / Skip'],
          });
          setStep('ask-colors');
        } else if (val === 'Sabhi Sizes (S-XXXL)') {
          const sizes = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
          setDraft(prev => ({ ...prev, sizes }));
          setPendingSizes(sizes);
          addBotMessage('Sizes: S–XXXL ✅\n\nColors available hain?', {
            type: 'quick-replies', quickReplies: [...COMMON_COLORS.map(c => c.name), 'No Colors / Skip'],
          });
          setStep('ask-colors');
        } else {
          const size = COMMON_SIZES.find(s => s.toLowerCase() === val.toLowerCase()) ?? val.toUpperCase();
          const newSizes = pendingSizes.includes(size) ? pendingSizes.filter(s => s !== size) : [...pendingSizes, size];
          setPendingSizes(newSizes);
          addBotMessage(
            newSizes.length === 0
              ? 'Koi size select nahi. Aur choose karo ya Done karo:'
              : `Selected: ${newSizes.join(', ')} ✅\n\nAur sizes add karo ya Done karo:`,
            {
              type: 'quick-replies',
              quickReplies: [...COMMON_SIZES.filter(s => !newSizes.includes(s)), 'Done — yahi sizes hain'],
            }
          );
        }
        break;
      }

      case 'ask-colors': {
        if (val === 'No Colors / Skip' || val === 'Skip') {
          setDraft(prev => ({ ...prev, colors: [] }));
          addBotMessage('Colors skip! ✅\n\nAb product ki photo upload karo:', {
            type: 'quick-replies', quickReplies: ['📸 Upload from device', '🔗 Image URL daalo'],
          });
          setStep('ask-image');
        } else {
          const colorNames = val.split(',').map(c => c.trim()).filter(Boolean);
          const colors: ColorVariant[] = colorNames.map(name => {
            const found = COMMON_COLORS.find(c => c.name.toLowerCase() === name.toLowerCase());
            return { name: found?.name ?? name, hex: found?.hex ?? '#888888', price: draft.price ?? 0, image: '', images: [], stock: 0 };
          });
          setDraft(prev => ({ ...prev, colors }));
          setPendingColorIdx(0);
          if (colors.length === 1) {
            addBotMessage(`Color: ${colors[0].name} ✅\n\nAb product ki photo upload karo:`, {
              type: 'quick-replies', quickReplies: ['📸 Upload from device', '🔗 Image URL daalo'],
            });
            setStep('ask-image');
          } else {
            addBotMessage(`${colors.length} colors! 🎨\n${colors.map((c, i) => `${i + 1}. ${c.name}`).join('\n')}\n\nHar color ki alag photo chahiye?`, {
              type: 'quick-replies', quickReplies: ['Har color ki alag photo chahiye', 'Same photo sabke liye'],
            });
            setStep('ask-color-images');
          }
        }
        break;
      }

      case 'ask-color-images': {
        if (val.toLowerCase().includes('alag') || val.toLowerCase().includes('different') || val.toLowerCase().includes('har color')) {
          const colors = draft.colors ?? [];
          if (colors.length > 0) {
            setPendingColorIdx(0);
            addBotMessage(`Theek hai! **${colors[0].name}** color ki photo upload karo:`, {
              type: 'quick-replies', quickReplies: ['📸 Upload from device', '🔗 Image URL daalo'],
            });
            setStep('ask-color-specific-image');
          } else { setStep('ask-image'); }
        } else {
          addBotMessage('Same photo sab colors ke liye! ✅\n\nProduct ki main photo upload karo:', {
            type: 'quick-replies', quickReplies: ['📸 Upload from device', '🔗 Image URL daalo'],
          });
          setStep('ask-image');
        }
        break;
      }

      case 'ask-color-specific-image': {
        const colors = draft.colors ?? [];
        const currentIdx = pendingColorIdx;
        if (val.toLowerCase().includes('upload') || val.toLowerCase().includes('device') || val.toLowerCase().includes('phone')) {
          fileInputRef.current?.click();
          return;
        }
        const url = val.trim();
        if (isValidImageUrl(url) || url.startsWith('http')) {
          const updatedColors = [...colors];
          if (updatedColors[currentIdx]) {
            updatedColors[currentIdx] = { ...updatedColors[currentIdx], image: url, images: [url] };
            setDraft(prev => ({ ...prev, colors: updatedColors }));
          }
          const nextIdx = currentIdx + 1;
          if (nextIdx < colors.length) {
            setPendingColorIdx(nextIdx);
            addBotMessage(`${colors[currentIdx].name} photo set! ✅\n\nAb **${colors[nextIdx].name}** color ki photo:`, {
              type: 'quick-replies', quickReplies: ['📸 Upload from device', '🔗 Image URL daalo'],
            });
          } else {
            addBotMessage(`Sab ${colors.length} colors ki photos set! ✅\n\nProduct ki main photo upload karo:`, {
              type: 'quick-replies', quickReplies: ['📸 Upload from device', '🔗 Image URL daalo'],
            });
            setStep('ask-image');
          }
        } else {
          addBotMessage('Valid image URL daao ya Upload button dabao!');
        }
        break;
      }

      case 'ask-image': {
        if (val.toLowerCase().includes('upload') || val.toLowerCase().includes('device') || val.toLowerCase().includes('phone') || val.toLowerCase().includes('camera')) {
          fileInputRef.current?.click();
          return;
        }
        const url = val.trim();
        if (!isValidImageUrl(url) && !url.startsWith('http')) {
          addBotMessage('Valid image URL daalo (.jpg/.png/.webp) ya Upload button dabao!');
          return;
        }
        setDraft(prev => ({ ...prev, images: [url], thumbnail: url }));
        setImagePreviewUrl(url);
        addBotMessage('Image mila! Dekh rahe hain...', { type: 'image-preview', imageUrl: url });
        setTimeout(() => {
          addBotMessage('Image preview above! Aur images add karni hain? (max 5):', {
            type: 'quick-replies',
            quickReplies: ['📸 Aur photos upload karo', '🔗 Image URL se', 'Nahi, yahi kaafi hai'],
          });
          setStep('ask-more-images');
        }, 600);
        break;
      }

      case 'ask-more-images': {
        if (val.toLowerCase().includes('upload') || val.toLowerCase().includes('device') || val.toLowerCase().includes('phone') || val.toLowerCase().includes('camera')) {
          fileInputRef.current?.click();
          return;
        }
        if (val.toLowerCase().includes('nahi') || val.toLowerCase().includes('no') || val.toLowerCase().includes('kaafi') || val.toLowerCase().includes('bas') || val.toLowerCase().includes('skip')) {
          const images = draft.images ?? [];
          addBotMessage(`${images.length} image(s) set! ✅\n\nAb stock batao:`, {
            type: 'quick-replies',
            quickReplies: [
              'Simple — ek total stock',
              ...(pendingSizes.length > 0 ? ['Per Size — har size ka alag stock'] : []),
              ...((draft.colors?.length ?? 0) > 0 ? ['Per Color — har color ka alag stock'] : []),
            ],
          });
          setStep('ask-stock-type');
        } else if (val.toLowerCase().includes('url') || val.toLowerCase().includes('link')) {
          addBotMessage('Image URL daalo:');
        } else {
          const url = val.trim();
          const currentImages = draft.images ?? [];
          if (currentImages.length >= 5) {
            addBotMessage('Maximum 5 images! Stock pe move karte hain.', { type: 'quick-replies', quickReplies: ['Stock set karo'] });
            setStep('ask-stock-type');
            return;
          }
          if (isValidImageUrl(url) || url.startsWith('http')) {
            setDraft(prev => ({ ...prev, images: [...(prev.images ?? []), url] }));
            addBotMessage(`Image ${currentImages.length + 1} add! ✅\n\nAur? (${currentImages.length + 1}/5)`, {
              type: 'quick-replies',
              quickReplies: ['📸 Aur photos upload karo', '🔗 Image URL se', 'Bas, yahi kaafi hai'],
            });
          } else {
            addBotMessage('Valid image URL daao ya Upload button dabao:', {
              type: 'quick-replies', quickReplies: ['📸 Upload from device', 'Bas, yahi kaafi hai'],
            });
          }
        }
        break;
      }

      case 'ask-stock-type': {
        if (val.includes('Simple')) {
          setStockType('simple');
          addBotMessage('Total stock kitna hai? (number daalo):');
          setStep('ask-stock-simple');
        } else if (val.includes('Per Size')) {
          setStockType('per-size');
          const sizes = pendingSizes.length > 0 ? pendingSizes : (draft.sizes ?? []);
          if (sizes.length === 0) {
            addBotMessage('Koi size nahi tha. Simple stock set karte hain:', { type: 'quick-replies', quickReplies: ['Simple — ek total stock'] });
            setStep('ask-stock-type'); return;
          }
          setSizeStockMap({});
          addBotMessage(`${sizes[0]} size ka stock kitna hai?`);
          setPendingColorIdx(0);
          setStep('ask-stock-per-size');
        } else if (val.includes('Per Color')) {
          setStockType('per-color');
          const colors = draft.colors ?? [];
          if (colors.length === 0) {
            addBotMessage('Colors set nahi. Simple stock set karte hain.', { type: 'quick-replies', quickReplies: ['Simple — ek total stock'] });
            setStep('ask-stock-type'); return;
          }
          setColorStockMap({});
          addBotMessage(`${colors[0].name} color ka stock kitna hai?`);
          setPendingColorIdx(0);
          setStep('ask-stock-per-color');
        }
        break;
      }

      case 'ask-stock-simple': {
        const stock = parseInt(val.replace(/[^0-9]/g, ''), 10);
        if (!stock || stock < 0) { addBotMessage('Valid stock number daalo:'); return; }
        const newDraft = { ...draft, stock, stockBreakdown: { total: stock } };
        setDraft(newDraft);
        askVideoStep(newDraft);
        break;
      }

      case 'ask-stock-per-size': {
        const sizes = pendingSizes.length > 0 ? pendingSizes : (draft.sizes ?? []);
        const stockNum = parseInt(val.replace(/[^0-9]/g, ''), 10) || 0;
        const currentIdx = Object.keys(sizeStockMap).length;
        const currentSize = sizes[currentIdx];
        const newMap = { ...sizeStockMap, [currentSize]: String(stockNum) };
        setSizeStockMap(newMap);
        if (currentIdx + 1 < sizes.length) {
          addBotMessage(`${currentSize}: ${stockNum} units ✅\n\n${sizes[currentIdx + 1]} size ka stock?`);
        } else {
          const totalStock = Object.values(newMap).reduce((sum, v) => sum + parseInt(v, 10), 0);
          const breakdown: Record<string, number> = {};
          sizes.forEach(s => { breakdown[s] = parseInt(newMap[s] ?? '0', 10); });
          const newDraft = { ...draft, stock: totalStock, stockBreakdown: breakdown };
          setDraft(newDraft);
          askVideoStep(newDraft);
        }
        break;
      }

      case 'ask-stock-per-color': {
        const colors = draft.colors ?? [];
        const stockNum = parseInt(val.replace(/[^0-9]/g, ''), 10) || 0;
        const currentIdx = Object.keys(colorStockMap).length;
        const currentColor = colors[currentIdx];
        const newMap = { ...colorStockMap, [currentColor.name]: String(stockNum) };
        setColorStockMap(newMap);
        if (currentIdx + 1 < colors.length) {
          addBotMessage(`${currentColor.name}: ${stockNum} units ✅\n\n${colors[currentIdx + 1].name} color ka stock?`);
        } else {
          const totalStock = Object.values(newMap).reduce((sum, v) => sum + parseInt(v, 10), 0);
          const breakdown: Record<string, number> = {};
          colors.forEach(c => { breakdown[c.name] = parseInt(newMap[c.name] ?? '0', 10); });
          const newDraft = { ...draft, stock: totalStock, stockBreakdown: breakdown };
          setDraft(newDraft);
          askVideoStep(newDraft);
        }
        break;
      }

      case 'ask-video': {
        if (val.toLowerCase().includes('upload') || val.toLowerCase().includes('video') || val.toLowerCase().includes('🎬')) {
          videoInputRef.current?.click();
          return;
        }
        if (val.toLowerCase().includes('skip') || val.toLowerCase().includes('nahi') || val.toLowerCase().includes('⏭')) {
          // No video, proceed to review
          showProductReview(draft);
        }
        break;
      }

      case 'review-product': {
        if (val.toLowerCase().includes('save') || val.toLowerCase().includes('list karo') || val.toLowerCase().includes('haan') || val.toLowerCase().includes('approved')) {
          saveProduct();
        } else if (val.toLowerCase().includes('nahi') || val.toLowerCase().includes('cancel') || val.toLowerCase().includes('band')) {
          addBotMessage('Theek hai, product cancel. Naya product add karna ho toh batao!', {
            type: 'quick-replies', quickReplies: ['Naya product add karo'],
          });
          setStep('welcome');
          setDraft({});
        }
        break;
      }

      default:
        break;
    }
  }, [step, draft, pendingSizes, sizeStockMap, colorStockMap, addBotMessage, pendingColorIdx, askVideoStep]);

  const showProductReview = useCallback((d: Partial<DraftProduct>) => {
    const discount = (d.originalPrice ?? 0) > (d.price ?? 0)
      ? Math.round((1 - (d.price ?? 0) / (d.originalPrice ?? 1)) * 100) : 0;

    addBotMessage('', {
      type: 'product-summary',
      text: 'Yeh raha product ka preview:',
      productDraft: d as DraftProduct,
    });

    setTimeout(() => {
      addBotMessage(
        `Product ready! ✅\n\n• Naam: ${d.name}\n• Category: ${d.category}\n• Price: ₹${d.price}${discount > 0 ? ` (${discount}% off)` : ''}\n• Sizes: ${(d.sizes ?? []).length > 0 ? (d.sizes ?? []).join(', ') : 'None'}\n• Colors: ${(d.colors ?? []).length > 0 ? d.colors!.map(c => c.name).join(', ') : 'None'}\n• Stock: ${d.stock ?? 0} units\n• Images: ${(d.images ?? []).length}\n• Video: ${d.video ? '✅ Added' : '❌ Not added'}\n\nProduct save karein?`,
        {
          type: 'quick-replies',
          quickReplies: ['Haan, save karo aur approve krunga ✅', 'Nahi, cancel karo'],
        }
      );
      setStep('review-product');
    }, 500);
  }, [addBotMessage]);

  const saveProduct = useCallback(async () => {
    setSaving(true);
    addBotMessage('Product save ho raha hai...');

    try {
      const newProduct = {
        name: draft.name ?? '',
        category: draft.category ?? '',
        price: draft.price ?? 0,
        original_price: draft.originalPrice ?? draft.price ?? 0,
        description: draft.description ?? '',
        sizes: draft.sizes ?? [],
        colors: draft.colors ?? [],
        stock: draft.stock ?? 0,
        stock_breakdown: draft.stockBreakdown ?? {},
        brand: draft.brand ?? '',
        tags: draft.tags ?? [],
        thumbnail: draft.thumbnail ?? (draft.images?.[0] ?? ''),
        images: draft.images ?? [],
        video: draft.video ?? null,
        video_thumbnail: draft.videoThumbnail ?? null,
        status: 'pending',
        is_trending: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase.from('products').insert([newProduct]);

      if (error) {
        console.error('Supabase insert error:', error);
        addBotMessage('Product save karne mein error aaya. Dobara try karo.', {
          type: 'quick-replies', quickReplies: ['Dobara save karo', 'Cancel'],
        });
        setSaving(false);
        return;
      }

      addBotMessage('Product successfully save ho gaya! 🎉\n\nAdmin Panel → Products → Pending Review mein jaao aur approve karo — tab customers ko dikhega!', {
        type: 'quick-replies',
        quickReplies: ['Naya product add karo 🆕', 'Products panel kholna hai'],
      });

      setStep('done');
      onProductAdded();
    } catch (err) {
      console.error('Save error:', err);
      addBotMessage('Kuch galat hua. Dobara try karo.', {
        type: 'quick-replies', quickReplies: ['Dobara save karo', 'Cancel'],
      });
    }

    setSaving(false);
  }, [draft, addBotMessage, onProductAdded]);

  const handleSizeDone = useCallback(() => {
    if (step !== 'ask-sizes') return;
    const sizes = pendingSizes.length > 0 ? pendingSizes : [];
    setDraft(prev => ({ ...prev, sizes }));
    addUserMessage('Done — yahi sizes hain');
    addBotMessage(`Sizes: ${sizes.length > 0 ? sizes.join(', ') : 'None'} ✅\n\nColors available hain?`, {
      type: 'quick-replies',
      quickReplies: [...COMMON_COLORS.map(c => c.name), 'Ek saath multiple colors batao', 'No Colors / Skip'],
    });
    setStep('ask-colors');
  }, [step, pendingSizes, addBotMessage, addUserMessage]);

  // Handle image file upload - MULTIPLE FILES
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const validFiles: File[] = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) { addBotMessage(`"${file.name}" image nahi hai.`); continue; }
      if (file.size > 8 * 1024 * 1024) { addBotMessage(`"${file.name}" 8MB se zyada hai.`); continue; }
      validFiles.push(file);
    }
    if (validFiles.length === 0) return;
    const currentImages = draft.images ?? [];
    const remainingSlots = 5 - currentImages.length;
    const filesToUpload = validFiles.slice(0, remainingSlots);
    if (filesToUpload.length === 0) {
      addBotMessage('Maximum 5 images already! Stock set karte hain.');
      askVideoStep(draft);
      return;
    }

    setUploadingImage(true);
    setUploadProgress(0);
    addBotMessage(`${filesToUpload.length} images upload ho rahi hain... ⏳`);

    const uploadedUrls: string[] = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      try {
        setUploadProgress(Math.round(((i + 0.3) / filesToUpload.length) * 100));
        const compressedBlob = await compressImage(file, 1600, 0.92);
        const compressedFile = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, '.jpg'), { type: 'image/jpeg' });
        setUploadProgress(Math.round(((i + 0.6) / filesToUpload.length) * 100));
        const fileName = `ai-bot-product-${Date.now()}-${generateId()}.jpg`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('product-images')
          .upload(fileName, compressedFile, { upsert: true, contentType: 'image/jpeg', cacheControl: '3600' });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(uploadData.path);
        uploadedUrls.push(urlData.publicUrl);
        setUploadProgress(Math.round(((i + 1) / filesToUpload.length) * 100));
      } catch {
        try {
          const reader = new FileReader();
          const dataUrl = await new Promise<string>((resolve, reject) => {
            reader.onload = (ev) => resolve(ev.target?.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          uploadedUrls.push(dataUrl);
        } catch { /* failed */ }
      }
    }

    if (uploadedUrls.length > 0) {
      const isFirstBatch = currentImages.length === 0;
      const newImages = [...currentImages, ...uploadedUrls];
      setDraft(prev => ({
        ...prev,
        images: newImages,
        thumbnail: isFirstBatch ? uploadedUrls[0] : prev.thumbnail,
      }));
      setImagePreviewUrl(uploadedUrls[0]);
      addBotMessage(`${uploadedUrls.length} images upload ho gayi! ✅`, {
        type: 'image-preview', imageUrl: uploadedUrls[0],
      });
      setTimeout(() => {
        if (newImages.length >= 5) {
          addBotMessage('Maximum 5 images! ✅\n\nAb stock batao:', {
            type: 'quick-replies',
            quickReplies: [
              'Simple — ek total stock',
              ...(pendingSizes.length > 0 ? ['Per Size — har size ka alag stock'] : []),
              ...((draft.colors?.length ?? 0) > 0 ? ['Per Color — har color ka alag stock'] : []),
            ],
          });
          setStep('ask-stock-type');
        } else {
          addBotMessage(`${newImages.length}/5 images! ✅\n\nAur images?`, {
            type: 'quick-replies',
            quickReplies: ['📸 Aur photos upload karo', '🔗 Image URL se', 'Bas, yahi kaafi hai'],
          });
          if (step === 'ask-image') setStep('ask-more-images');
        }
      }, 800);
    }

    setUploadingImage(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [draft, pendingSizes, step, addBotMessage, askVideoStep]);

  // ── Handle VIDEO upload ──
  const handleVideoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    if (!file.type.startsWith('video/')) {
      addBotMessage('Yeh video file nahi hai! MP4 ya WebM upload karo.');
      return;
    }
    // Validate size (20MB)
    if (file.size > 20 * 1024 * 1024) {
      addBotMessage('Video 20MB se zyada hai! Compress karke try karo.\n\nSkip karein?', {
        type: 'quick-replies', quickReplies: ['⏭️ Skip — Video Nahi Chahiye'],
      });
      return;
    }

    setUploadingVideo(true);
    setVideoProgress(0);
    addBotMessage('🎬 Video upload ho rahi hai... thoda wait karo ⏳');

    try {
      setVideoProgress(20);
      const ext = file.name.split('.').pop() ?? 'mp4';
      const fileName = `product-video-${Date.now()}-${generateId()}.${ext}`;

      setVideoProgress(40);
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('product-videos')
        .upload(fileName, file, { upsert: true, contentType: file.type, cacheControl: '3600' });

      if (uploadError) throw uploadError;

      setVideoProgress(80);
      const { data: urlData } = supabase.storage.from('product-videos').getPublicUrl(uploadData.path);
      const videoUrl = urlData.publicUrl;

      // Use thumbnail as first product image
      const videoThumbnail = draft.thumbnail ?? draft.images?.[0] ?? '';

      setDraft(prev => ({ ...prev, video: videoUrl, videoThumbnail }));
      setVideoProgress(100);

      addBotMessage('🎬 Video upload ho gayi! ✅', {
        type: 'video-preview',
        text: '🎬 Video upload ho gayi! ✅',
        videoUrl,
      });

      setTimeout(() => {
        addBotMessage('Video set ho gayi! Ab product ka full preview dekhte hain:');
        showProductReview({ ...draft, video: videoUrl, videoThumbnail });
      }, 800);

    } catch (err) {
      console.error('Video upload error:', err);
      addBotMessage('Video upload fail hua. Storage bucket setup nahi ho sakta. Video URL paste karo ya skip karo:', {
        type: 'quick-replies',
        quickReplies: ['⏭️ Skip — Video Nahi Chahiye'],
      });
    }

    setUploadingVideo(false);
    setVideoProgress(0);
    if (videoInputRef.current) videoInputRef.current.value = '';
  }, [draft, addBotMessage, showProductReview]);

  const handleSend = () => {
    const val = input.trim();
    if (!val || saving) return;
    addUserMessage(val);
    handleInput(val);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl" style={{ height: '88vh', maxHeight: 700 }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 bg-gradient-to-r from-slate-900 to-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center shadow-lg shrink-0">
              <i className="ri-robot-2-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm leading-none">AI Product Assistant</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse shrink-0"></span>
                <p className="text-slate-400 text-xs">Photos + Video Upload Ready</p>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-400 hover:bg-slate-600 hover:text-white transition-all cursor-pointer shrink-0">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
              {msg.role === 'bot' && (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center shrink-0 mt-1">
                  <i className="ri-robot-2-line text-white text-xs"></i>
                </div>
              )}
              <div className={`max-w-[82%]`}>
                {msg.type === 'image-preview' && msg.imageUrl && (
                  <div className="mb-2 rounded-xl overflow-hidden border border-gray-200 bg-white" style={{ width: 160, height: 200 }}>
                    <img src={msg.imageUrl} alt="Product preview" className="w-full h-full object-cover"
                      onError={e => { (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=product%20clothing%20white%20background&width=160&height=200&seq=bot01&orientation=portrait'; }} />
                  </div>
                )}
                {/* VIDEO PREVIEW in chat */}
                {msg.type === 'video-preview' && msg.videoUrl && (
                  <div className="mb-2 rounded-xl overflow-hidden border-2 border-purple-200 bg-black" style={{ width: 200, height: 140 }}>
                    <video src={msg.videoUrl} className="w-full h-full object-contain" controls playsInline muted preload="metadata" />
                  </div>
                )}
                {msg.type === 'product-summary' && msg.productDraft && (
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-2 shadow-sm" style={{ width: 220 }}>
                    <div style={{ height: 160 }} className="bg-gray-100 overflow-hidden">
                      {msg.productDraft.thumbnail ? (
                        <img src={msg.productDraft.thumbnail} alt={msg.productDraft.name} className="w-full h-full object-cover"
                          onError={e => { (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=clothing%20product%20white%20background&width=220&height=160&seq=bot02&orientation=landscape'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><i className="ri-image-line text-3xl text-gray-300"></i></div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-gray-900 font-bold text-sm">{msg.productDraft.name}</p>
                      <p className="text-gray-400 text-xs">{msg.productDraft.category}{msg.productDraft.brand ? ` · ${msg.productDraft.brand}` : ''}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <p className="text-gray-900 font-bold text-base">₹{msg.productDraft.price}</p>
                        <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold border border-amber-200">Pending</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {msg.productDraft.sizes?.slice(0, 3).map(s => (
                          <span key={s} className="px-2 py-0.5 rounded-lg bg-gray-100 text-gray-600 text-[10px] font-semibold">{s}</span>
                        ))}
                        {(msg.productDraft.sizes?.length ?? 0) > 3 && (
                          <span className="text-gray-400 text-[10px]">+{(msg.productDraft.sizes?.length ?? 0) - 3} more</span>
                        )}
                      </div>
                      {msg.productDraft.video && (
                        <div className="mt-2 flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-purple-50 border border-purple-100">
                          <i className="ri-video-fill text-purple-500 text-xs"></i>
                          <span className="text-purple-700 text-[10px] font-semibold">5-sec video added ✓</span>
                        </div>
                      )}
                      <p className="text-gray-500 text-[10px] mt-1.5 flex items-center gap-1">
                        <i className="ri-stack-line"></i>{msg.productDraft.stock ?? 0} units in stock
                      </p>
                    </div>
                  </div>
                )}
                {msg.text && (
                  <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                    msg.role === 'user'
                      ? 'bg-slate-800 text-white rounded-tr-md'
                      : 'bg-white text-gray-800 border border-gray-100 rounded-tl-md shadow-sm'
                  }`}>
                    {msg.text}
                  </div>
                )}
                {msg.type === 'quick-replies' && msg.quickReplies && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {msg.quickReplies.map(reply => {
                      const isDoneReply = reply.includes('Done — yahi sizes hain');
                      return (
                        <button key={reply}
                          onClick={() => isDoneReply ? handleSizeDone() : handleQuickReply(reply)}
                          className="px-3 py-1.5 rounded-xl border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-800 hover:text-white hover:border-slate-800 transition-all cursor-pointer whitespace-nowrap">
                          {reply}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
          {(saving || uploadingVideo) && (
            <div className="flex justify-start gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center shrink-0">
                <i className="ri-robot-2-line text-white text-xs"></i>
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  <span className="text-gray-500 text-xs">{uploadingVideo ? 'Uploading video...' : 'Saving product...'}</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="px-4 py-3 border-t border-gray-100 bg-white shrink-0">
          {/* Image Upload Progress */}
          {uploadingImage && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 font-medium">Uploading images...</span>
                <span className="text-xs text-gray-500">{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-orange-400 to-pink-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            </div>
          )}
          {/* Video Upload Progress */}
          {uploadingVideo && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-purple-600 font-semibold flex items-center gap-1">
                  <i className="ri-video-upload-line"></i> Uploading video...
                </span>
                <span className="text-xs text-purple-600">{videoProgress}%</span>
              </div>
              <div className="h-2 bg-purple-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300" style={{ width: `${videoProgress}%` }}></div>
              </div>
            </div>
          )}

          {/* Hidden file inputs */}
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" disabled={uploadingImage || saving} />
          <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/*" onChange={handleVideoUpload} className="hidden" disabled={uploadingVideo || saving} />

          <div className="flex items-center gap-2">
            {/* Image Upload Button */}
            {(step === 'ask-image' || step === 'ask-more-images' || step === 'ask-color-specific-image') && (
              <button onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage || saving}
                className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white shrink-0 hover:shadow-lg transition-all cursor-pointer disabled:opacity-40"
                title="Upload photos from device">
                <i className="ri-camera-fill text-lg"></i>
              </button>
            )}
            {/* Video Upload Button */}
            {step === 'ask-video' && (
              <button onClick={() => videoInputRef.current?.click()}
                disabled={uploadingVideo || saving}
                className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white shrink-0 hover:shadow-lg transition-all cursor-pointer disabled:opacity-40 animate-pulse"
                title="Upload 5-second video from device">
                <i className="ri-video-upload-line text-lg"></i>
              </button>
            )}

            <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                step === 'ask-video'
                  ? '🎬 Video upload karo ya Skip dabao...'
                  : step === 'ask-image' || step === 'ask-more-images' || step === 'ask-color-specific-image'
                    ? '📸 Image URL paste karo ya camera dabao...'
                    : step === 'ask-stock-simple' || step === 'ask-stock-per-size' || step === 'ask-stock-per-color' || step === 'ask-price' || step === 'ask-original-price'
                      ? 'Number daalo...'
                      : 'Type karo ya upar se select karo...'
              }
              disabled={saving || uploadingImage || uploadingVideo}
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:opacity-50" />
            <button onClick={handleSend}
              disabled={!input.trim() || saving || uploadingImage || uploadingVideo}
              className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center text-white shrink-0 hover:bg-slate-800 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
              <i className="ri-send-plane-fill text-sm"></i>
            </button>
          </div>

          {/* Helper footer text */}
          <p className="text-center text-gray-400 text-[10px] mt-2">
            {step === 'ask-video'
              ? '🎬 Purple button se 5-sec video upload karo (max 20MB · MP4/WebM)'
              : step === 'ask-image' || step === 'ask-more-images' || step === 'ask-color-specific-image'
                ? '📸 Camera icon se phone se photo upload karo, ya URL paste karo'
                : 'AI bot product draft banayega — aap approve karoge tabhi live hoga'}
          </p>
        </div>
      </div>
    </div>
  );
}
